import { inject, Injectable } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { BehaviorSubject } from 'rxjs';
import { PushNotificationsApi } from '../apis/push-notifications.api';
import { ClientInfo } from '../interfaces/client-info';
import { SiaI18nService } from './i18n.service';
import { SiaUsersService } from './users.service';

@Injectable({
  providedIn: 'root',
})
export class SiaPushNotificationsService {
  #pushNotificationsApi = inject(PushNotificationsApi);
  #userService = inject(SiaUsersService);
  #i18nService = inject(SiaI18nService);
  #notificationGranted = false;
  #messaging: any;
  #pushNotificationsStatus = new BehaviorSubject<{
    success?: boolean;
    silentMode?: boolean;
    message?: string;
  }>({ silentMode: true });

  public pushNotificationsStatus$ =
    this.#pushNotificationsStatus.asObservable();

  #firebaseConfig = {
    apiKey: 'AIzaSyCY1ds76qEao0F43ZSgl6BqBuvj4jImaho',
    authDomain: 'vapidpushnotifications.firebaseapp.com',
    projectId: 'vapidpushnotifications',
    storageBucket: 'vapidpushnotifications.firebasestorage.app',
    messagingSenderId: '711627976495',
    appId: '1:711627976495:web:a3a4497f595495dd4afbda',
    measurementId: 'G-VQ07SJPLD4',
  };

  async initFirebaseMessaging(silentMode: boolean): Promise<boolean> {
    try {
      // Check if messaging is supported (important for Safari)
      const supported = await isSupported();
      if (!supported) {
        console.warn('Firebase messaging is not supported on this browser');
        this.#pushNotificationsStatus.next({
          success: false,
          silentMode,
          message: this.#i18nService.translate(
            'Notifiche push non supportate su questo browser',
          ),
        });
        return false;
      }

      // Check if we're in a secure context (required for service workers)
      if (!('serviceWorker' in navigator) || !window.isSecureContext) {
        console.warn('Service workers not supported or not in secure context');
        this.#pushNotificationsStatus.next({
          success: false,
          silentMode,
          message: this.#i18nService.translate(
            'Service Workers non supportati o non in un contesto sicuro (HTTPS)',
          ),
        });
        return false;
      }

      const app = initializeApp(this.#firebaseConfig);
      this.#messaging = getMessaging(app);
      return true;
    } catch (error) {
      console.error('Error initializing Firebase messaging:', error);
      this.#pushNotificationsStatus.next({
        success: false,
        silentMode,
        message: this.#i18nService.translate(
          'Errore di inizializzazione di Firebase Messaging',
        ),
      });
    }
    return false;
  }

  async requestNotificationPermission(silentMode: boolean): Promise<boolean> {
    try {
      // Check if notifications are supported
      if (!('Notification' in window)) {
        console.warn('This browser does not support notifications');
        this.#pushNotificationsStatus.next({
          success: false,
          silentMode,
          message: this.#i18nService.translate(
            'Questo browser non supporta le notifiche',
          ),
        });
        return false;
      }

      // Check current permission
      if (Notification.permission === 'granted') {
        this.#notificationGranted = true;
        this.#pushNotificationsStatus.next({
          success: true,
          silentMode,
          message: this.#i18nService.translate(
            'Permesso di notifica già concesso',
          ),
        });
        await this.getFCMToken(silentMode);
        return true;
      }

      if (Notification.permission === 'denied') {
        console.warn('Notification permission denied');
      }

      // Request permission (this should be called after user interaction)
      const permission = await Notification.requestPermission();
      this.#notificationGranted = permission === 'granted';

      if (this.#notificationGranted) {
        //console.log('Notification permission granted');
        this.#pushNotificationsStatus.next({
          success: true,
          silentMode,
          message: this.#i18nService.translate('Permesso di notifica concesso'),
        });
        await this.getFCMToken(silentMode);
      } else {
        //console.log('Notification permission denied');
        this.#pushNotificationsStatus.next({
          success: false,
          silentMode,
          message: this.#i18nService.translate('Permesso di notifica negato'),
        });
      }

      return this.#notificationGranted;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      this.#pushNotificationsStatus.next({
        success: false,
        silentMode,
        message: this.#i18nService.translate(
          'Errore durante la richiesta del permesso di notifica',
        ),
      });
      return false;
    }
  }

  private async getFCMToken(silentMode: boolean): Promise<void> {
    try {
      if (!this.#messaging) {
        console.error('Firebase messaging not initialized');
        this.#pushNotificationsStatus.next({
          success: false,
          silentMode,
          message: this.#i18nService.translate(
            'Firebase Messaging non inizializzato',
          ),
        });
        return;
      }

      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        console.error('No service worker registration found');
        this.#pushNotificationsStatus.next({
          success: false,
          silentMode,
          message: this.#i18nService.translate(
            'Nessuna registrazione del service worker trovata',
          ),
        });
        return;
      }

      const currentToken = await getToken(this.#messaging, {
        serviceWorkerRegistration: registration,
        vapidKey:
          'BFT25gqKaKC5FT1pdrjLvt-CN85ZpAGQskvb7AX2jKPELn9bne2n9XgfRBefpMzEBrVDvN6KdZ82lQ46oaX7PKs',
      });

      if (currentToken) {
        //console.log('FCM registration token:', currentToken);
        this.#pushNotificationsStatus.next({
          success: true,
          silentMode,
          message: this.#i18nService.translate(
            'Token di registrazione FCM ottenuto con successo',
          ),
        });
        await this.sendTokenToServer(currentToken);
      } else {
        //console.log('No FCM registration token available');
        this.#pushNotificationsStatus.next({
          success: false,
          silentMode,
          message: this.#i18nService.translate(
            'Nessun token di registrazione FCM disponibile',
          ),
        });
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      this.#pushNotificationsStatus.next({
        success: false,
        silentMode,
        message: this.#i18nService.translate(
          "Errore durante l'ottenimento del token FCM",
        ),
      });
    }
  }

  private async sendTokenToServer(token: string): Promise<void> {
    try {
      // Wait until we have a real user id before proceeding
      const userId = await this.#waitForUserId();
      if (!userId) {
        console.warn('Skipping push subscription: userId not available');
        return;
      }

      const info = this.getClientInfo();
      info.token = token;
      info.notifyPermissionGranted = this.#notificationGranted;

      const response = await this.#pushNotificationsApi.subscribe(info);
    } catch (error) {
      console.error('Error sending token to server:', error);
    }
  }

  // Waits for the authenticated userId to become available, polling briefly.
  // If timeoutMs is provided, returns null after the timeout; otherwise waits indefinitely.
  async #waitForUserId(
    timeoutMs?: number,
    intervalMs: number = 500,
  ): Promise<number | null> {
    const deadline = timeoutMs ? Date.now() + timeoutMs : undefined;
    let uid = this.#userService.getRealUserId();
    while (!uid) {
      if (deadline && Date.now() >= deadline) {
        return null;
      }
      await this.#delay(intervalMs);
      uid = this.#userService.getRealUserId();
    }
    return uid;
  }

  #delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Call this method after user interaction (e.g., button click)
  async enableNotifications(silentMode: boolean = false): Promise<boolean> {
    var done = await this.initFirebaseMessaging(silentMode);
    if (done) {
      return await this.requestNotificationPermission(silentMode);
    }
    return done;
  }

  // Enhanced browser detection for mobile devices
  getClientInfo(): ClientInfo {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    const screenResolution = `${window.screen.width}x${window.screen.height}`;

    let browserName = 'Unknown';
    let browserVersion = 'Unknown';
    let deviceType = 'Desktop';

    // Enhanced mobile detection
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent,
      );
    const isTablet =
      /iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)|Tablet/i.test(userAgent);

    if (isMobile && !isTablet) {
      deviceType = 'Mobile';
    } else if (isTablet) {
      deviceType = 'Tablet';
    }

    // Enhanced browser detection
    if (/CriOS\/(\d+)/i.test(userAgent)) {
      browserName = 'Chrome iOS';
      browserVersion = userAgent.match(/CriOS\/(\d+)/i)![1];
    } else if (/FxiOS\/(\d+)/i.test(userAgent)) {
      browserName = 'Firefox iOS';
      browserVersion = userAgent.match(/FxiOS\/(\d+)/i)![1];
    } else if (/Safari/i.test(userAgent) && /iPhone|iPad/i.test(userAgent)) {
      browserName = 'Safari iOS';
      browserVersion =
        userAgent.match(/Version\/(\d+\.\d+)/i)?.[1] || 'Unknown';
    } else if (/chrome\/(\d+\.\d+)/i.test(userAgent)) {
      browserName = 'Chrome';
      browserVersion = userAgent.match(/chrome\/(\d+\.\d+)/i)![1];
    } else if (/firefox\/(\d+\.\d+)/i.test(userAgent)) {
      browserName = 'Firefox';
      browserVersion = userAgent.match(/firefox\/(\d+\.\d+)/i)![1];
    } else if (
      /safari\/(\d+\.\d+)/i.test(userAgent) &&
      !/chrome/i.test(userAgent)
    ) {
      browserName = 'Safari';
      browserVersion =
        userAgent.match(/version\/(\d+\.\d+)/i)?.[1] || 'Unknown';
    } else if (/edg\/(\d+\.\d+)/i.test(userAgent)) {
      browserName = 'Edge';
      browserVersion = userAgent.match(/edg\/(\d+\.\d+)/i)![1];
    }

    return {
      platform,
      browserName,
      browserVersion,
      userAgent,
      language,
      screenResolution,
      deviceType,
      token: '', // Will be set later
      notifyPermissionGranted: this.#notificationGranted,
    };
  }
}
